import {
  Component,
  ComponentInterface,
  h,
  Prop,
  State,
  Method,
} from "@stencil/core";
import {
  DeviceService,
  ThemeService,
  TranslateService,
} from "../../../services";
import { IThemeOptions } from "../../../models";

@Component({
  tag: "av-button",
  styleUrl: "av-button.scss",
  shadow: true,
})
export class AvButton implements ComponentInterface {
  @Prop() type;
  @Prop() loading: boolean;
  @Prop() shape: "round" | "flat" | "bordered" = "flat";
  @Prop() color:
    | "primary"
    | "basic"
    | "accent"
    | "link"
    | "danger"
    | "success"
    | "warning";
  @Prop() tooltip: string;
  @Prop() tooltipPosition: "left" | "right";
  @Prop() disabled: boolean;
  @Prop() link: string;
  @Prop() size: "xs" | "sm" | "md" | "lg" | "block" = "lg";
  @Prop() background: boolean;
  @Prop() ariaLabel: string;
  @Prop() ariaLabelledBy: string;

  @State() themeOptions: IThemeOptions;

  @Method()
  async setFocus() {
    this._btnRef.focus();
  }

  private brandPrimaryColor = "#305084";
  private isMobile = DeviceService.isMobile;
  private _btnRef: HTMLButtonElement;

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );
  }

  render() {
    const buttonStyle = {
      borderColor: undefined,
      color: this.color === "link" && this.themeOptions.colorAccent,
      background:
        (this.color === "accent" && this.themeOptions.colorAccent) ||
        (this.color === "primary" &&
          `linear-gradient(135deg, ${this.themeOptions.colorPrimary}, ${this.brandPrimaryColor} )`),
    };
    if (this.shape == "bordered" && this.color == "link") {
      buttonStyle.borderColor = this.themeOptions.colorAccent;
    }
    const darkSpinner =
      (["link", "basic"].includes(this.color) && !this.themeOptions.darkMode) ||
      (this.disabled && this.color !== "accent");
    const buttonClass = {
      "av-button": true,
      "av-button-loading": this.loading,
      "av-button-flat": this.shape == "flat",
      "av-button-round": this.shape === "round",
      "av-button-bordered": this.shape === "bordered",
      "av-button-mobile": this.isMobile,

      "av-button-color-basic": this.color === "basic",
      "av-button-color-link": this.color === "link",
      "av-button-color-accent": this.color === "accent",
      "av-button-color-primary": this.color === "primary" || !this.color,
      "av-button-color-danger": this.color === "danger",
      "av-button-color-success": this.color === "success",
      "av-button-color-warning": this.color === "warning",

      "av-button-size-lg": this.size === "lg",
      "av-button-size-md": this.size === "md",
      "av-button-size-sm": this.size === "sm",
      "av-button-size-xs": this.size === "xs",
      "av-button-size-block": this.size === "block",

      "av-button-background": this.background,
    };
    return !!this.link ? (
      <a
        aria-labelledby={this.ariaLabelledBy}
        aria-label={this.ariaLabel}
        href={this.link}
        target="_blank"
        title={this.tooltip}
        class={buttonClass}
        style={buttonStyle}
      >
        {this.loading && <av-spinner dark={darkSpinner}></av-spinner>}
        {this.tooltip && (
          <span
            class={{
              "av-button-tooltip": true,
              "av-button-tooltip-position-right":
                this.tooltipPosition == "right",
              "av-button-tooltip-position-left": this.tooltipPosition == "left",
              // "av-button-tooltip-dark": this.themeOptions.darkMode,
            }}
          >
            {TranslateService.instant(this.tooltip)}
          </span>
        )}
        <slot></slot>
      </a>
    ) : (
      <button
        aria-label={this.ariaLabel}
        aria-labelledby={this.ariaLabelledBy}
        title={this.tooltip}
        class={buttonClass}
        style={buttonStyle}
        type={this.type}
        disabled={this.loading || this.disabled}
        ref={(el) => (this._btnRef = el)}
      >
        {this.loading && <av-spinner dark={darkSpinner}></av-spinner>}
        {this.tooltip && (
          <span
            class={{
              "av-button-tooltip": true,
              "av-button-tooltip-position-right":
                this.tooltipPosition == "right",
              "av-button-tooltip-position-left": this.tooltipPosition == "left",
              // "av-button-tooltip-dark": this.themeOptions.darkMode,
            }}
          >
            {TranslateService.instant(this.tooltip)}
          </span>
        )}
        <slot></slot>
      </button>
    );
  }
}
